import React, { useContext } from 'react';
import { useMutation, TypedDocumentNode } from '@apollo/client';
import { Button, Stack, Typography, InputAdornment, Box, Alert, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useForm } from 'react-hook-form';

import { useModal } from '/packages/shared-components/utils/modal';
import TextInput from '/packages/shared-components/forms/TextInput/TextInput';
import { componentColors } from '/packages/shared-components/utils/color';
import { FlashMessageContext } from '/packages/shared-components/utils/flashMessage';
import { DialogTitle, DialogContent, DialogActions, DialogContainer } from '/packages/shared-components/Dialog/Dialog';
import { getBackgroundColor, getContentColor } from '/packages/shared-components/utils/color';

import { CREATE_INTERNAL_USER_ACCESS_REQUEST } from '/apps/portal/src/queries/codegen/createInternalUserAccessRequest';
import { createInternalUserAccessRequestMutation } from '/apps/portal/src/generated/portal/graphql';

type TRequestAcessForm = {
  email: string;
  businessJustification: string;
  rostrLink: string;
};

const RequestAccessModal = () => {
  const { showErrorMessage, showSuccessMessage } = useContext(FlashMessageContext);
  const { handleModalClose } = useModal();
  const {
    handleSubmit,
    control,
    trigger,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm<TRequestAcessForm>({ defaultValues: { email: '', businessJustification: '', rostrLink: '' } });

  const [createRequest] = useMutation(
    CREATE_INTERNAL_USER_ACCESS_REQUEST as TypedDocumentNode<createInternalUserAccessRequestMutation>,
    {
      onCompleted: () => {
        showSuccessMessage('User Access Request has been successfully sent.');
        handleModalClose();
      },
      onError: (error) => {
        showErrorMessage(error);
        handleModalClose();
      },
    },
  );
  const theme = useTheme();
  const submitForm = async () => {
    const isValid = await trigger();

    if (isValid) {
      const values = getValues();

      return createRequest({
        variables: {
          input: {
            email: values.email,
            businessJustification: values.businessJustification,
            rostrLink: values.rostrLink,
          },
        },
      });
    }
  };

  return (
    <DialogContainer>
      <DialogTitle sx={{ textAlign: 'center' }} variant="h6">
        Request User Access
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%' }} overflow="auto" height="auto">
          <form
            onSubmit={handleSubmit(submitForm)}
            style={{
              flex: 1,
              overflowY: 'auto',
              width: '100%',
            }}
            id="request-access-form"
          >
            <Stack gap={3} sx={{ paddingTop: '0.5rem', px: 0 }}>
              <Typography variant="body2">
                Use this option to request access on behalf of a Disney colleague. The invited user will be notified
                only if their request is approved by a member of the Technical Account Management (TAM) team.
              </Typography>
              <Alert
                severity="info"
                variant="filled"
                sx={{
                  color: getContentColor(theme.palette.info),
                  backgroundColor: getBackgroundColor(theme.palette.info),
                  lineHeight: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                You can only request access for Disney users. If a Partner needs access, please contact the TAM team.
              </Alert>
              <TextInput
                label="User Email"
                name="email"
                type="email"
                placeholder="Add an email address"
                control={control}
                required
                helperText="Valid email addresses only. Ex. name@disney.com"
                hideHelperTextOnError
                customErrorMessages={{
                  email: 'Valid email addresses only. Ex. name@disney.com',
                }}
                validate={{
                  email: (value: string) =>
                    /^[a-zA-Z0-9._%+-]+@disney\.com$/.test(value) || 'Valid email addresses only. Ex. name@disney.com',
                }}
              />

              <TextInput
                label="Business Justification"
                name="businessJustification"
                type="text"
                placeholder="As a [Role], I need access to manage [task] which will help improve [outcome]."
                control={control}
                required
                maxLength={250}
                rows={4}
              />
              <TextInput
                label="Rostr Profile"
                name="rostrLink"
                type="url"
                placeholder="Add a Rostr profile link"
                control={control}
                required
                endAdornment={
                  <InputAdornment
                    position="end"
                    sx={(theme) => ({
                      borderLeft: `1px solid ${
                        errors.rostrLink
                          ? theme.palette.error.main
                          : componentColors(theme.palette.primary).outlinedBorder
                      }`,
                      mr: -1.75,
                      maxHeight: '100%',
                      height: '56px',
                      width: '122px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'stretch',
                      transition: 'background 0.2s',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '& .MuiOutlinedInput-root': {
                        paddingRight: 0,
                      },
                    })}
                  >
                    <Button
                      variant="text"
                      size="large"
                      sx={{
                        height: '100%',
                        width: '122px',
                        background: 'none',
                        boxShadow: 'none',
                      }}
                      href="https://rostr.disney.com"
                      target="_blank"
                    >
                      Rostr
                      <OpenInNewIcon fontSize="small" sx={{ marginLeft: 1 }} />
                    </Button>
                  </InputAdornment>
                }
              />
            </Stack>
          </form>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2} justifyContent="flex-end">
          <Button
            size="small"
            variant="text"
            onClick={handleModalClose}
            sx={{
              color: 'text.primary',
              padding: '4px 12px',
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button size="small" form="request-access-form" variant="contained" type="submit" disabled={isSubmitting}>
            Request Access
          </Button>
        </Stack>
      </DialogActions>
    </DialogContainer>
  );
};

export default RequestAccessModal;
